// frontend/src/components/UserProfile.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';

const UserProfile = ({ user, onClose }) => {
  const { logout } = useAuth();
  const [profileData, setProfileData] = useState({
    displayName: user.displayName || '',
    email: user.email || '',
    photoURL: user.photoURL || ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState('');

  // Load user statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await ApiService.getUserProfile();
        if (response.success) {
          const statsResponse = await fetch('/api/members/stats/overview', {
            headers: {
              'Authorization': `Bearer ${await user.getIdToken()}`
            }
          });
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            setStats(statsData.stats);
          }
        }
      } catch (error) {
        console.error('Error loading user stats:', error);
      }
    };

    loadStats();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!profileData.displayName.trim()) {
      setMessage('Name cannot be empty');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await ApiService.updateUserProfile({
        displayName: profileData.displayName.trim(),
        photoURL: profileData.photoURL.trim() || null
      });

      if (response.success) {
        setMessage('Profile updated successfully! ✅');
        setIsEditing(false);
        
        // Update Firebase profile as well
        await user.updateProfile({
          displayName: profileData.displayName.trim(),
          photoURL: profileData.photoURL.trim() || null
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile. Please try again.');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
      setMessage('Error logging out. Please try again.');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Auto-hide messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#92400e' }}>
            👤 User Profile
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            ×
          </button>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            margin: '16px 24px',
            padding: '12px',
            backgroundColor: message.includes('✅') ? '#dcfce7' : '#fee2e2',
            border: `1px solid ${message.includes('✅') ? '#bbf7d0' : '#fecaca'}`,
            color: message.includes('✅') ? '#166534' : '#dc2626',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        <div style={{ padding: '24px' }}>
          {/* Profile Picture */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#fbbf24',
              border: '3px solid #92400e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              margin: '0 auto 16px',
              overflow: 'hidden'
            }}>
              {profileData.photoURL ? (
                <img 
                  src={profileData.photoURL} 
                  alt={profileData.displayName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                '👤'
              )}
            </div>
            {!isEditing && (
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                {profileData.displayName}
              </h3>
            )}
          </div>

          {/* Profile Form */}
          {isEditing ? (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Display Name
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={profileData.displayName}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  placeholder="Enter your name"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Profile Photo URL (optional)
                </label>
                <input
                  type="url"
                  name="photoURL"
                  value={profileData.photoURL}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  placeholder="https://example.com/photo.jpg"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  style={{
                    flex: 1,
                    backgroundColor: loading ? '#9ca3af' : '#d97706',
                    color: 'white',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setProfileData({
                      displayName: user.displayName || '',
                      email: user.email || '',
                      photoURL: user.photoURL || ''
                    });
                    setMessage('');
                  }}
                  style={{
                    flex: 1,
                    backgroundColor: '#6b7280',
                    color: 'white',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Profile Info */}
              <div style={{
                backgroundColor: '#fef3c7',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#92400e' }}>Email:</strong>
                  <div style={{ color: '#6b7280' }}>{profileData.email}</div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#92400e' }}>Member Since:</strong>
                  <div style={{ color: '#6b7280' }}>{formatDate(user.metadata?.creationTime)}</div>
                </div>
                <div>
                  <strong style={{ color: '#92400e' }}>Last Sign In:</strong>
                  <div style={{ color: '#6b7280' }}>{formatDate(user.metadata?.lastSignInTime)}</div>
                </div>
              </div>

              {/* Family Tree Statistics */}
              {stats && (
                <div style={{
                  backgroundColor: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#92400e', marginBottom: '12px' }}>
                    📊 Your Family Tree
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#d97706' }}>
                        {stats.totalMembers}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Members</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#d97706' }}>
                        {stats.uniqueClans}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Clan Names</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#d97706' }}>
                        {stats.withBirthDates}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>With Birth Dates</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#d97706' }}>
                        {stats.withPhotos}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>With Photos</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>✏️</span>
                  Edit Profile
                </button>

                <button
                  onClick={handleLogout}
                  style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>🚪</span>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;